<template>
  <div class="page-wrapper">
    <div v-if="subscriptionStatus === 'success'" class="success-wrapper">
      <section class="hero-panel">
        <img :src="require(`../../brand/${this.brandConfig.brand}/assets/evina-img.webp`)" class="hero-image" alt="Battle League Gaming hero image">
        <div class="hero-shadow"></div>
        <div class="overlay-image-wrapper">
          <img :src="require(`../../brand/${this.brandConfig.brand}/assets/logo-caption.png`)" class="overlay-image" alt="Battle League Gaming">
        </div>
      </section>

      <section class="content-panel">
        <div class="success-header">
          Success!
        </div>
        <div class="success-message">
          Thank you for joining {{ this.brandConfig.tabTitle }}!
        </div>

        <div class="cta-link" @click="redirectToPlatform">
          Go to {{ this.brandConfig.tabTitle }}
        </div>
      </section>
    </div>

    <div v-else-if="subscriptionStatus === 'error'" class="error-wrapper">
      <section class="hero-panel">
        <div class="error-hero-background"></div>
      </section>

      <section class="content-panel error-content-panel">
        <div class="error-icon">&times;</div>
        <div class="error-header">
          Subscription Failed
        </div>
        <div class="error-message">
          {{ errorDetails.message }}
        </div>
        <div class="error-code">
          Error Code: {{ errorDetails.code }}
        </div>
      </section>
    </div>

    <div v-else class="loading-wrapper">
      <section class="hero-panel">
        <div class="loading-hero-background"></div>
      </section>

      <section class="content-panel loading-content-panel">
        <div class="spinner"></div>
        <div class="loading-header">
          Processing your subscription...
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { trackEvent } from "@/functions/trackEvent";

export default {
  data() {
    return {
      subscriptionStatus: "loading",
      errorDetails: {
        code: null,
        message: null,
      },
    };
  },
  computed: {
    ...mapState(["brandConfig"]),
  },
  methods: {
    parseUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);

      return {
        status: urlParams.get("status"),
        code: urlParams.get("code"),
        message: urlParams.get("message"),
      };
    },
    async handleSubscriptionResponse() {
      const params = this.parseUrlParams();

      if (params.status === "error") {
        this.subscriptionStatus = "error";
        this.errorDetails = {
          code: params.code || "UNKNOWN",
          message: params.message || "An unexpected error occurred. Please try again.",
        };
        return;
      }

      this.subscriptionStatus = "success";
      trackEvent("subscription_success");
    },
    redirectToPlatform() {
      const msisdn = this.$store.state.msisdn;
      const platformUrl = msisdn
        ? `${this.brandConfig.platformUrl}?msisdn=${encodeURIComponent(msisdn)}`
        : this.brandConfig.platformUrl;
      window.location.href = platformUrl;
    },
  },
  async mounted() {
    await this.handleSubscriptionResponse();
  },
};
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background: #020202;
  color: #FFFFFF;
  display: flex;
  flex-direction: column;
}

.success-wrapper,
.error-wrapper,
.loading-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.hero-panel {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  min-height: 64vh;
  max-height: 74vh;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.hero-shadow {
  position: absolute;
  inset: 0;
  background:
      linear-gradient(180deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0) 14%, rgba(0, 0, 0, 0.18) 58%, rgba(2, 2, 2, 0.96) 100%),
      linear-gradient(180deg, rgba(2, 2, 2, 0) 0%, rgba(2, 2, 2, 0.9) 92%, #020202 100%);
}

.error-hero-background,
.loading-hero-background {
  width: 100%;
  min-height: 64vh;
  max-height: 74vh;
  background: linear-gradient(180deg, #1a1a1a 0%, #020202 100%);
  display: block;
}

.overlay-image-wrapper {
  position: absolute;
  left: 50%;
  bottom: 25%;
  width: min(84%, 360px);
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 2;
}

.overlay-image {
  width: 100%;
  height: auto;
  display: block;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.5));
}

.content-panel {
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  margin-top: -8rem;
  padding: 0 1.15rem 2rem;
  position: relative;
  z-index: 3;
}

.error-content-panel,
.loading-content-panel {
  margin-top: -4rem;
  padding: 2rem 1.15rem;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.success-header {
  color: #FFFFFF;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
}

.success-message {
  color: #C4C4C4;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  max-width: 20rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
}

.cta-link {
  color: #B3F50D;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  text-decoration: underline;
  transition: opacity 0.3s ease;
}

.cta-link:hover {
  opacity: 0.8;
}

.error-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  background: #f44336;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  font-weight: bold;
}

.error-header {
  color: #FFFFFF;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
}

.error-message {
  color: #C4C4C4;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  max-width: 20rem;
}

.error-code {
  color: #FFFFFF;
  text-align: center;
  font-size: 0.9rem;
  opacity: 0.8;
  font-family: monospace;
}

.spinner {
  width: 60px;
  height: 60px;
  margin: 0 auto;
  border: 6px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-header {
  color: #FFFFFF;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (min-width: 768px) {
  .hero-image,
  .error-hero-background,
  .loading-hero-background {
    min-height: 70vh;
  }

  .content-panel {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
</style>
